// ========================================================================
// SocialOS SDK - WebSocket Real-Time Connection Manager
// ========================================================================

import { WebSocketMessage, WebSocketConnectionState, WebSocketEventType } from './types';
import { ErrorHandler } from './errors';

export class WebSocketManager {
  private url: string;
  private socket: WebSocket | null = null;
  private state: WebSocketConnectionState;
  private messageHandlers: Map<string, Set<(data: unknown) => void>> = new Map();
  private reconnectTimeout: number | null = null;
  private heartbeatInterval: number | null = null;
  private errorHandler: ErrorHandler;
  private connectionId: string | null = null;

  constructor(url: string) {
    this.url = url;
    this.errorHandler = ErrorHandler.getInstance();
    this.state = {
      connected: false,
      connecting: false,
      reconnecting: false,
      lastConnected: null,
      reconnectAttempts: 0,
      error: null
    };
  }

  connect(): void {
    if (this.state.connected || this.state.connecting) {
      return;
    }

    this.state.connecting = true;
    this.state.error = null;

    try {
      const wsUrl = this.buildWebSocketUrl();
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => this.handleOpen();
      this.socket.onmessage = (event) => this.handleMessage(event);
      this.socket.onclose = (event) => this.handleClose(event);
      this.socket.onerror = (error) => this.handleError(error);
    } catch (error) {
      this.errorHandler.handle(error as Error);
      this.state.connecting = false;
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.clearReconnectTimeout();
    this.clearHeartbeat();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }

    this.updateState({
      connected: false,
      connecting: false,
      reconnecting: false
    });
  }

  send(event: WebSocketEventType, payload: unknown): void {
    if (!this.state.connected || !this.socket) {
      throw new Error('WebSocket is not connected');
    }

    const message: WebSocketMessage = {
      event,
      payload,
      timestamp: Date.now(),
      connectionId: this.connectionId || undefined
    };

    this.socket.send(JSON.stringify(message));
  }

  subscribe<T>(event: string, callback: (data: T) => void): () => void {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, new Set());
    }

    this.messageHandlers.get(event)!.add(callback);

    return () => {
      this.unsubscribe(event, callback);
    };
  }

  unsubscribe(event: string, callback: (data: unknown) => void): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.delete(callback);
    }
  }

  getState(): WebSocketConnectionState {
    return { ...this.state };
  }

  isConnected(): boolean {
    return this.state.connected;
  }

  private buildWebSocketUrl(): string {
    const url = new URL(this.url);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${url.host}/api/realtime`;
  }

  private handleOpen(): void {
    this.updateState({
      connected: true,
      connecting: false,
      reconnecting: false,
      lastConnected: Date.now(),
      reconnectAttempts: 0,
      error: null
    });

    this.startHeartbeat();
    this.emit('connection', { connected: true });
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      this.connectionId = message.connectionId || this.connectionId;

      const handlers = this.messageHandlers.get(message.event);
      if (handlers) {
        handlers.forEach((callback) => {
          try {
            callback(message.payload);
          } catch (error) {
            this.errorHandler.handle(error as Error);
          }
        });
      }

      // Emit global message event
      this.emit('message', message);
    } catch (error) {
      this.errorHandler.handle(error as Error);
    }
  }

  private handleClose(__event: CloseEvent): void {
    this.clearHeartbeat();

    const wasConnected = this.state.connected;
    this.updateState({
      connected: false,
      connecting: false
    });

    if (wasConnected) {
      this.emit('connection', { connected: false });
      this.scheduleReconnect();
    }
  }

  private handleError(__error: Event): void {
    const errorObj = new Error('WebSocket error');
    this.errorHandler.handle(errorObj);
    this.updateState({ error: errorObj });
  }

  private scheduleReconnect(): void {
    if (this.state.reconnecting) {
      return;
    }

    this.updateState({ reconnecting: true });

    const delay = Math.min(1000 * Math.pow(2, this.state.reconnectAttempts), 30000);
    this.state.reconnectAttempts++;

    this.reconnectTimeout = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      if (this.state.connected && this.socket) {
        try {
          this.send(WebSocketEventType.PRESENCE, { type: 'ping' });
        } catch {
          // Ignore heartbeat errors
        }
      }
    }, 30000);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private updateState(updates: Partial<WebSocketConnectionState>): void {
    this.state = { ...this.state, ...updates };
    this.emit('stateChange', this.state);
  }

  private emit(event: string, data: unknown): void {
    const handlers = this.messageHandlers.get(event);
    if (handlers) {
      handlers.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          this.errorHandler.handle(error as Error);
        }
      });
    }
  }
}

// ============ WebSocket Hook Helper ============

export function createWebSocketHook(
  url: string,
  _eventHandlers: Record<string, (data: unknown) => void>
) {
  const manager = new WebSocketManager(url);

  return {
    connect: () => manager.connect(),
    disconnect: () => manager.disconnect(),
    send: (event: WebSocketEventType, payload: unknown) => manager.send(event, payload),
    subscribe: <T>(event: string, callback: (data: T) => void) => 
      manager.subscribe(event, callback),
    getState: () => manager.getState(),
    isConnected: () => manager.isConnected()
  };
}
